"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { FiPlus, FiMinus } from "react-icons/fi";
import { FAQ_DATA, FAQItem } from "@/data/faq-about-page";

export function FAQSection() {
  // Store the ID of the open FAQ item (starts with "02" expanded to match the mockup)
  const [openId, setOpenId] = useState<string | null>("faq-2");

  const toggleFAQ = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="w-full bg-[#f8f9fa] py-20 px-6 sm:px-12 lg:px-20">
      <div className="mx-auto max-w-[1440px] grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
        
        {/* ─── LEFT SIDE CONTENT ─── */}
        <div className="flex flex-col items-start sticky top-12">
          {/* Badge */}
          <span className="inline-block px-4 py-1.5 rounded-full border border-neutral-300 text-sm font-medium text-neutral-600 bg-white shadow-sm mb-6">
            Our faq's
          </span>

          {/* Typography Header */}
          <h2 className="text-[clamp(2.5rem,5vw,4.5rem)] font-bold text-neutral-950 leading-[1.1] tracking-tight mb-8">
            Frequently asked <br />
            <span className="font-serif italic font-normal tracking-wide block mt-1">
              questions!
            </span>
          </h2>

          {/* CTA Button */}
          <button className="group flex items-center gap-4 bg-[#d4e117] hover:bg-[#c3cf13] text-neutral-900 font-semibold px-6 py-4 rounded-full shadow-md transition-all duration-300">
            <span>Learn More</span>
            <span className="w-2 h-2 rounded-full bg-neutral-950 block transition-transform group-hover:scale-125" />
          </button>
        </div>

        {/* ─── RIGHT SIDE ACCORDION CONTAINER ─── */}
        <div className="bg-[#f1f3f5] p-4 sm:p-6 rounded-3xl flex flex-col gap-3 shadow-inner">
          {FAQ_DATA.map((item: FAQItem) => {
            const isOpen = openId === item.id;

            return (
              <div
                key={item.id}
                className={clsx(
                  "w-full rounded-2xl bg-white border border-neutral-100 transition-shadow duration-300 overflow-hidden",
                  isOpen ? "shadow-md" : "shadow-sm hover:shadow-md"
                )}
              >
                {/* Trigger Button */}
                <button
                  onClick={() => toggleFAQ(item.id)}
                  className="w-full flex items-center justify-between text-left p-6 gap-4 cursor-pointer select-none"
                  aria-expanded={isOpen}
                >
                  <span className="text-base sm:text-lg font-bold text-neutral-900 leading-snug">
                    <span className="mr-3 tracking-tight font-medium text-neutral-900/90">
                      {item.number}.
                    </span>
                    {item.question}
                  </span>

                  {/* Icon Indicator Wrapper */}
                  <div
                    className={clsx(
                      "flex items-center justify-center w-8 h-8 rounded-full border shrink-0 transition-colors duration-300",
                      isOpen
                        ? "bg-[#eb6d2f] border-[#eb6d2f] text-white"
                        : "bg-white border-neutral-300 text-neutral-800"
                    )}
                  >
                    {isOpen ? (
                      <FiMinus className="w-4 h-4 stroke-[3]" />
                    ) : (
                      <FiPlus className="w-4 h-4 stroke-[3]" />
                    )}
                  </div>
                </button>

                {/* Smooth Expandable Content */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                        transition: {
                          height: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                          opacity: { duration: 0.25, ease: "linear" },
                        },
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: {
                          height: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
                          opacity: { duration: 0.15, ease: "linear" },
                        },
                      }}
                    >
                      <div className="px-6 pb-6 pt-1 text-sm sm:text-base leading-relaxed text-neutral-500 max-w-[90%] font-normal">
                        {item.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}